/**
 * Subscription utility functions for checking user payment status and trial state.
 */

export interface UserSubscriptionData {
  subscription_status?: string;
  subscription_tier?: string;
  trial_ends_at?: string | null;
  discount_percentage?: number | null;
}

/**
 * Trial duration in days.
 */
export const TRIAL_DURATION_DAYS = 14;

/**
 * Discount percentage at (or above) which an account is treated as fully comped.
 */
const FULLY_COMPED_DISCOUNT_PERCENTAGE = 100;

/**
 * Check if a user is fully comped (100% discount) — e.g. signed up from a
 * partner domain like Kintsugi. Comped users have permanent free access and
 * are never asked for a credit card.
 *
 * @param userData - User data with discount information
 * @returns true if the user has a 100% (or greater) discount
 */
export function isComped(userData: UserSubscriptionData): boolean {
  return (userData.discount_percentage ?? 0) >= FULLY_COMPED_DISCOUNT_PERCENTAGE;
}

/**
 * Check if a user's trial has expired.
 *
 * @param userData - User data with trial information
 * @returns true if trial has expired, false otherwise
 */
export function isTrialExpired(userData: UserSubscriptionData): boolean {
  // Comped users (100% discount) have permanent access and never expire
  if (isComped(userData)) {
    return false;
  }

  // If the cron has already marked the user as trial_expired, respect that
  if (userData.subscription_status === 'trial_expired') {
    return true;
  }

  // If no trial_ends_at, trial hasn't started or user is on a paid plan
  if (!userData.trial_ends_at) {
    return false;
  }

  const trialEndDate = new Date(userData.trial_ends_at);
  const now = new Date();

  return now > trialEndDate;
}

/**
 * Check if a user is currently in an active trial.
 *
 * @param userData - User data with subscription information
 * @returns true if user is in active trial, false otherwise
 */
export function isInActiveTrial(userData: UserSubscriptionData): boolean {
  const status = userData.subscription_status || 'active';
  const tier = userData.subscription_tier || 'free';

  // Must be trialing status and not expired
  return status === 'trialing' && tier === 'monthly' && !isTrialExpired(userData);
}

/**
 * Check if a user has an active paid subscription (not a trial).
 *
 * @param userData - User data with subscription information
 * @returns true if user has active paid subscription (not trial), false otherwise
 */
export function hasActiveSubscription(userData: UserSubscriptionData): boolean {
  const status = userData.subscription_status || 'active';
  const tier = userData.subscription_tier || 'free';

  // Active subscription means 'active' status (not 'trialing') with a paid tier
  return status === 'active' && tier === 'monthly';
}

/**
 * Check if a user has an active paid subscription.
 * A user is considered "paid" if:
 * - subscription_status is 'active' (has a valid paid subscription), OR
 * - subscription_status is 'trialing' AND trial has NOT expired
 *
 * @param userData - User data with subscription information
 * @returns true if user has active paid subscription or active trial, false otherwise
 */
export function isUserPaid(userData: UserSubscriptionData): boolean {
  // Comped users (100% discount) always have access without paying
  if (isComped(userData)) {
    return true;
  }

  const status = userData.subscription_status || 'active';
  const tier = userData.subscription_tier || 'free';

  // Not on a paid tier
  if (tier !== 'monthly') {
    return false;
  }

  // Active subscription (not trial)
  if (status === 'active') {
    return true;
  }

  // Trialing - check if trial hasn't expired
  if (status === 'trialing') {
    return !isTrialExpired(userData);
  }

  return false;
}

/**
 * Check if a user should be redirected to onboarding/upgrade.
 * Users should be redirected if:
 * - They don't have a paid subscription AND
 * - They're not in an active trial
 *
 * @param userData - User data with subscription information
 * @returns true if user should be redirected to onboarding, false otherwise
 */
export function shouldRedirectToOnboarding(userData: UserSubscriptionData): boolean {
  return !isUserPaid(userData);
}

/**
 * Check if a user's trial is expiring soon (within specified days).
 *
 * @param userData - User data with trial information
 * @param daysThreshold - Number of days to consider "soon" (default: 3)
 * @returns true if trial is expiring within threshold, false otherwise
 */
export function isTrialExpiringSoon(userData: UserSubscriptionData, daysThreshold = 3): boolean {
  // Only applicable for trialing users
  if (userData.subscription_status !== 'trialing') {
    return false;
  }

  if (!userData.trial_ends_at) {
    return false;
  }

  const trialEndDate = new Date(userData.trial_ends_at);
  const now = new Date();

  // Already expired
  if (now > trialEndDate) {
    return false;
  }

  // Calculate days remaining
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysRemaining = Math.ceil((trialEndDate.getTime() - now.getTime()) / msPerDay);

  return daysRemaining <= daysThreshold;
}

/**
 * Get the number of days remaining in the trial.
 *
 * @param userData - User data with trial information
 * @returns Number of days remaining (0 if expired or not applicable)
 */
export function getTrialDaysRemaining(userData: UserSubscriptionData): number {
  if (!userData.trial_ends_at) {
    return 0;
  }

  const trialEndDate = new Date(userData.trial_ends_at);
  const now = new Date();

  if (now > trialEndDate) {
    return 0;
  }

  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.ceil((trialEndDate.getTime() - now.getTime()) / msPerDay);
}

/**
 * Get subscription status display information.
 *
 * @param userData - User data with subscription information
 * @returns Object with status, tier, trial info, and access flags
 */
export function getSubscriptionInfo(userData: UserSubscriptionData): {
  status: string;
  tier: string;
  isPaid: boolean;
  isTrialing: boolean;
  isTrialExpired: boolean;
  trialDaysRemaining: number;
  hasActiveSubscription: boolean;
} {
  const status = userData.subscription_status || 'active';
  const tier = userData.subscription_tier || 'free';
  const isTrialing = status === 'trialing';
  const trialExpired = isTrialExpired(userData);
  const daysRemaining = getTrialDaysRemaining(userData);

  return {
    status,
    tier,
    isPaid: isUserPaid(userData),
    isTrialing,
    isTrialExpired: trialExpired,
    trialDaysRemaining: daysRemaining,
    hasActiveSubscription: hasActiveSubscription(userData),
  };
}
